import React, {
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./MapGrid.css";
import { Case } from "../../model/Case";
import {
  Popper,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Button,
  Tooltip,
  Drawer,
  IconButton,
  FormGroup,
  FormControlLabel,
  Switch,
  AppBar,
  Toolbar,
  TextField,
} from "@mui/material";
import BuildingForm from "../BuildingForm/BuildingForm";
import { baseUrl } from "../../constants";
import useAxios from "../../hooks/useAxios";
import SearchIcon from "@mui/icons-material/Search";
import HomeIcon from "@mui/icons-material/Home";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import DecorationModal from "../BuildingForm/DecorationModal";
import axios from "axios";
import PresenceModal from "../BuildingForm/PresenceModal";
import SectorContext from "../../context/SectorContext";
import FilterListIcon from "@mui/icons-material/FilterList";
import TuneIcon from "@mui/icons-material/Tune";

function MapGrid() {
  interface PopperPosition {
    x: number;
    y: number;
  }

  const [tiles, setBatiments] = useState<Case[]>([]);
  const [decorations, setDecorations] = useState<any>({});
  const [showDecorationModal, setShowDeclarationModal] =
    useState<boolean>(false);
  const [showPresenceModal, setShowPresenceModal] = useState<boolean>(false);
  const [searchTiles, setSearchTiles] = useState<Case[]>([]);
  const [searchParams, setSearchParams] = useState({
    request: "",
    proprietaire: true,
    locataire: true,
    membre: true,
    employe: true,
    presence: true,
    note: true,
    adresse: true,
    nom: true,
  });
  const [hoveredTile, setHoveredTile] = useState<Case>();
  const [popperPosition, setPopperPosition] = useState<PopperPosition>();
  const [anchorEl, setAnchorEl] = useState<SVGElement>();
  const [clickedTile, setClickedTile] = useState<Case>();

  const [axiosInstance] = useAxios();

  const [openBuildingTypeFilterDrawer, setOpenBuildingTypeFilterDrawer] =
    useState(false);
  const [openSearchCriteriaDrawer, setOpenSearchCriteriaDrawer] =
    useState(false);
  const [mainSearchQuery, setMainSearchQuery] = useState("");
  const [satelliteIdsInput, setSatelliteIdsInput] = useState("");

  const { sector } = useContext(SectorContext);

  // State for building type visibility
  const [showCercle, setShowCercle] = useState(true);
  const [showEntreprise, setShowEntreprise] = useState(true);
  const [showAnnexe, setShowAnnexe] = useState(true);
  const [showLieuPublic, setShowLieuPublic] = useState(true);
  const [showOi, setShowOi] = useState(true);
  const [showTcast, setShowTcast] = useState(true);
  const [showLogement, setShowLogement] = useState(true);
  const [showEmptyLots, setShowEmptyLots] = useState(true); // New state for empty lots filter

  const handleMouseMove = (event: SyntheticEvent<SVGElement, MouseEvent>) => {
    const x = Math.floor(event.nativeEvent.offsetX / TILE_SIZE) + viewBoxMin;
    const y = Math.floor(event.nativeEvent.offsetY / TILE_SIZE) + viewBoxMin;
    const tile = grid[x][y];

    if (hoveredTile === tile) {
      return;
    }

    setAnchorEl(event.currentTarget);
    setPopperPosition({
      x:
        (Math.floor(event.nativeEvent.offsetX / TILE_SIZE) + viewBoxMin) *
        TILE_SIZE,
      y:
        (Math.floor(event.nativeEvent.offsetY / TILE_SIZE) + viewBoxMin) *
        TILE_SIZE,
    });
    setHoveredTile(tile);
  };

  const handleMouseClick = (event: SyntheticEvent<SVGElement, MouseEvent>) => {
    const x = Math.floor(event.nativeEvent.offsetX / TILE_SIZE) + viewBoxMin;
    const y = Math.floor(event.nativeEvent.offsetY / TILE_SIZE) + viewBoxMin;
    const tile = grid[x][y];
    if (!tile) return;

    axiosInstance
      .get(`${baseUrl}/case/${tile.id}`)
      .then((x) => x.data)
      .then((x) => {
        if (x.lotId !== clickedTile?.id) {
          setClickedTile(x);
        }
      });
  };

  const handleSearch = async (currentSearchParams: any) => {
    setSearchParams(currentSearchParams); // Update searchParams state with the latest query and options
    const searchBatiment = await axiosInstance.get<Case[]>(
      `${baseUrl}/lots/search`,
      {
        params: {
          secteurId: sector,
          ...currentSearchParams,
        },
      }
    );
    setSearchTiles(searchBatiment.data);
  };

  const handleResetSearch = async () => {
    setSearchTiles([]);
    setMainSearchQuery("");
    setSearchParams({
      request: "",
      proprietaire: true,
      locataire: true,
      membre: true,
      employe: true,
      presence: true,
      note: true,
      adresse: true,
      nom: true,
    });
  };

  const handleClose = async () => {
    setClickedTile(undefined);
    const batiments = await axiosInstance.get<Case[]>(
      `${baseUrl}/lots?id=${sector}`
    );
    setBatiments(batiments.data);
  };

  const handleShowDecoration = () => {
    setShowDeclarationModal(true);
  };
  const handleCloseDecoration = () => {
    setShowDeclarationModal(false);
  };

  const handleShowPresence = () => {
    setShowPresenceModal(true);
  };

  const handleClosePresence = () => {
    setShowPresenceModal(false);
  };

  useEffect(() => {
    (async () => {
      const batiments = await axiosInstance.get<Case[]>(
        `${baseUrl}/lots?id=${sector}`
      );
      setBatiments(batiments.data);
      const decoration = await axios.get("FullWall Admins.json", {
        responseType: "json",
        headers: {
          "Content-Type": "application/json",
        },
      });
      setDecorations(
        decoration.data.batiment.reduce(
          (acc: any, x: any[]) => ({ ...acc, [x[0]]: x[1] }),
          {}
        )
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sector]);

  const TILE_SIZE = 20;
  const openPopper = Boolean(hoveredTile);
  const openModal = Boolean(clickedTile);

  /*return <div className="grid">
       {tiles.map((tile) => {
           return (<div key={tile.id} style={{gridColumnStart : tile.x, gridRowStart: tile.y}} className={tile.batiment?.categorie?.toLocaleLowerCase().replace(/\W+/g, '-') ?? "node"}></div>)
       })}
   </div>*/

  const grid = Array.from({ length: 100 }, () => [] as Case[]);
  tiles.forEach((t) => (grid[t.x][t.y] = t));

  const SATELLITE_COVERAGE_SIZE = 15;
  const satelliteCoveragePositions = useMemo(() => {
    if (!satelliteIdsInput.trim()) {
      return [];
    }
    const parsedIds = satelliteIdsInput
      .split(",")
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isFinite(value));
    if (!parsedIds.length) {
      return [];
    }
    const lookup = tiles.reduce<Record<number, Case>>((acc, tile) => {
      acc[tile.lotId] = tile;
      return acc;
    }, {} as Record<number, Case>);
    return Array.from(new Set(parsedIds)).reduce<
      { x: number; y: number; tileId: number }[]
    >((acc, id) => {
      const tile = lookup[id];
      if (tile) {
        acc.push({ x: tile.x, y: tile.y, tileId: tile.id });
      }
      return acc;
    }, []);
  }, [satelliteIdsInput, tiles]);

  const tilePath = ({ x, y }: { x: number; y: number }) =>
    `M ${x},${y} h 1 v 1 h -1 v -1 `;

  // Path generation logic
  let cerclePath = "";
  let entreprisePath = "";
  let annexePath = "";
  let lieuPublicPath = "";
  let oiPath = "";
  let tcastPath = "";
  let logementPath = "";
  let hiddenFilteredPath = ""; // Used when no search is active, for type-filtered items
  let grayedOutNonSearchPath = ""; // Used when search is active, for non-search items
  let emptyPath = "";

  if (searchTiles.length > 0) {
    // SEARCH IS ACTIVE
    const searchResultIds = new Set(searchTiles.map((t) => t.id));
    tiles.forEach((tile) => {
      const pathSegment = tilePath(tile);
      if (searchResultIds.has(tile.id)) {
        // IT IS A SEARCH RESULT
        if (tile.batiment === null || tile.batiment === undefined) {
          emptyPath += pathSegment; // Will be styled by searchPath overlay
        } else {
          // Assign to its category path for base color, will be styled by searchPath overlay
          switch (tile.batiment?.categorie) {
            case "Cercle":
              cerclePath += pathSegment;
              break;
            case "Entreprise":
              entreprisePath += pathSegment;
              break;
            case "Annexe":
              annexePath += pathSegment;
              break;
            case "Lieu Public":
              lieuPublicPath += pathSegment;
              break;
            case "Organisation Impériale":
              oiPath += pathSegment;
              break;
            case "T-Cast":
              tcastPath += pathSegment;
              break;
            case "Logement":
              logementPath += pathSegment;
              break;
            default:
              // If a search result has an unknown category, it will just be highlighted by the animated searchPath.
              break;
          }
        }
      } else {
        // NOT A SEARCH RESULT - gray it out (applies to both buildings and empty lots not in search)
        grayedOutNonSearchPath += pathSegment;
      }
    });
  } else {
    // NO SEARCH ACTIVE - use type filters
    tiles.forEach((tile) => {
      const pathSegment = tilePath(tile);
      if (tile.batiment === null || tile.batiment === undefined) {
        if (showEmptyLots) {
          // Apply the new filter for empty lots
          emptyPath += pathSegment;
        } else {
          hiddenFilteredPath += pathSegment;
        }
      } else {
        // It's a building, apply category filters
        switch (tile.batiment?.categorie) {
          case "Cercle":
            if (showCercle) cerclePath += pathSegment;
            else hiddenFilteredPath += pathSegment;
            break;
          case "Entreprise":
            if (showEntreprise) entreprisePath += pathSegment;
            else hiddenFilteredPath += pathSegment;
            break;
          case "Annexe":
            if (showAnnexe) annexePath += pathSegment;
            else hiddenFilteredPath += pathSegment;
            break;
          case "Lieu Public":
            if (showLieuPublic) lieuPublicPath += pathSegment;
            else hiddenFilteredPath += pathSegment;
            break;
          case "Organisation Impériale":
            if (showOi) oiPath += pathSegment;
            else hiddenFilteredPath += pathSegment;
            break;
          case "T-Cast":
            if (showTcast) tcastPath += pathSegment;
            else hiddenFilteredPath += pathSegment;
            break;
          case "Logement":
            if (showLogement) logementPath += pathSegment;
            else hiddenFilteredPath += pathSegment;
            break;
          default: // Handles unknown categories
            hiddenFilteredPath += pathSegment;
            break;
        }
      }
    });
  }
  // End of path generation logic

  const searchPath = searchTiles.map(tilePath).join("");

  const viewBoxMin = 3;
  const viewBoxSize = 97;
  const extraRightPaddingTiles = 2;
  const svgViewBoxWidth = viewBoxSize + extraRightPaddingTiles;
  const svgWidth = TILE_SIZE * svgViewBoxWidth;
  const svgHeight = TILE_SIZE * viewBoxSize;

  const mapRef = useRef();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowX: "hidden",
        minHeight: 0,
      }}
    >
      <AppBar position="sticky">
        <Toolbar>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<FilterListIcon />}
            onClick={() => setOpenBuildingTypeFilterDrawer(true)}
            sx={{ mr: 1 }}
          >
            Filtres
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<TuneIcon />}
            onClick={() => setOpenSearchCriteriaDrawer(true)}
            sx={{ mr: 2 }}
          >
            Options de recherche
          </Button>
          <TextField
            label="Rechercher"
            variant="outlined"
            size="small"
            value={mainSearchQuery}
            onChange={(e) => setMainSearchQuery(e.target.value)}
            sx={{
              backgroundColor: "common.white",
              borderRadius: 1,
              flexGrow: 1,
              mr: 1,
            }}
            onKeyPress={(ev) => {
              if (ev.key === "Enter") {
                handleSearch({ ...searchParams, request: mainSearchQuery });
                ev.preventDefault();
              }
            }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() =>
              handleSearch({ ...searchParams, request: mainSearchQuery })
            }
            sx={{ mr: 1 }}
          >
            Rechercher
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleResetSearch}
          >
            Reset
          </Button>
        </Toolbar>
      </AppBar>

      {/* Drawer for Building Type Filters */}
      <Drawer
        anchor="top"
        open={openBuildingTypeFilterDrawer}
        onClose={() => setOpenBuildingTypeFilterDrawer(false)}
        PaperProps={{ sx: { mt: "64px", p: 2 } }} // Adjust mt if AppBar height differs
      >
        <Typography variant="h6" gutterBottom>
          Filtrer par type de bâtiment
        </Typography>
        <FormGroup sx={{ display: "flex", flexDirection: "column" }}>
          <FormControlLabel
            control={
              <Switch
                checked={showCercle}
                onChange={(e) => setShowCercle(e.target.checked)}
              />
            }
            label="Cercle"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showEntreprise}
                onChange={(e) => setShowEntreprise(e.target.checked)}
              />
            }
            label="Entreprise"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showAnnexe}
                onChange={(e) => setShowAnnexe(e.target.checked)}
              />
            }
            label="Annexe"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showLieuPublic}
                onChange={(e) => setShowLieuPublic(e.target.checked)}
              />
            }
            label="Lieu Public"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showOi}
                onChange={(e) => setShowOi(e.target.checked)}
              />
            }
            label="Organisation Impériale"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showTcast}
                onChange={(e) => setShowTcast(e.target.checked)}
              />
            }
            label="T-Cast"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showLogement}
                onChange={(e) => setShowLogement(e.target.checked)}
              />
            }
            label="Logement"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showEmptyLots}
                onChange={(e) => setShowEmptyLots(e.target.checked)}
              />
            }
            label="Empty Lots" // New Switch for Empty Lots
          />
          <TextField
            placeholder="Séparer les ids par des virgules"
            variant="standard"
            label="Recherche Satellite"
            value={satelliteIdsInput}
            onChange={(e) => setSatelliteIdsInput(e.target.value)}
          />
        </FormGroup>
        <Button
          onClick={() => setOpenBuildingTypeFilterDrawer(false)}
          sx={{ mt: 2 }}
        >
          Fermer
        </Button>
      </Drawer>

      {/* Drawer for Search Criteria */}
      <Drawer
        anchor="top"
        open={openSearchCriteriaDrawer}
        onClose={() => setOpenSearchCriteriaDrawer(false)}
        PaperProps={{ sx: { mt: "64px", p: 2 } }} // Adjust mt if AppBar height differs
      >
        <Typography variant="h6" gutterBottom>
          Options de recherche
        </Typography>
        <FormGroup sx={{ display: "flex", flexDirection: "column" }}>
          <FormControlLabel
            control={
              <Switch
                checked={searchParams.proprietaire}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    proprietaire: e.target.checked,
                  }))
                }
              />
            }
            label="Propriétaire"
          />
          <FormControlLabel
            control={
              <Switch
                checked={searchParams.locataire}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    locataire: e.target.checked,
                  }))
                }
              />
            }
            label="Locataire"
          />
          <FormControlLabel
            control={
              <Switch
                checked={searchParams.membre}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    membre: e.target.checked,
                  }))
                }
              />
            }
            label="Membre"
          />
          <FormControlLabel
            control={
              <Switch
                checked={searchParams.employe}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    employe: e.target.checked,
                  }))
                }
              />
            }
            label="Employé"
          />
          <FormControlLabel
            control={
              <Switch
                checked={searchParams.presence}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    presence: e.target.checked,
                  }))
                }
              />
            }
            label="Présence"
          />
          <FormControlLabel
            control={
              <Switch
                checked={searchParams.note}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    note: e.target.checked,
                  }))
                }
              />
            }
            label="Note"
          />
          <FormControlLabel
            control={
              <Switch
                checked={searchParams.adresse}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    adresse: e.target.checked,
                  }))
                }
              />
            }
            label="Adresse"
          />
          <FormControlLabel
            control={
              <Switch
                checked={searchParams.nom}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    nom: e.target.checked,
                  }))
                }
              />
            }
            label="Nom du bâtiment"
          />
        </FormGroup>
        <Button
          onClick={() => setOpenSearchCriteriaDrawer(false)}
          sx={{ mt: 2 }}
        >
          Fermer
        </Button>
      </Drawer>

      {/* Map content takes remaining space */}
      <Box
        className="mapContainer"
        ref={mapRef}
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowX: "scroll",
          overflowY: "auto",
          p: 1,
        }}
      >
        <svg
          viewBox={`${viewBoxMin} ${viewBoxMin} ${svgViewBoxWidth} ${viewBoxSize}`}
          width={svgWidth}
          height={svgHeight}
          onMouseMove={handleMouseMove}
          onClick={handleMouseClick}
        >
          {/* Render truly empty tiles first */}
          <path
            d={emptyPath}
            fill="#999999"
            stroke="black"
            strokeWidth="0.01"
          />
          {/* Render grayed-out tiles: either non-search results or type-filtered tiles */}
          {searchTiles.length > 0 ? (
            <path
              d={grayedOutNonSearchPath}
              fill="#E0E0E0" // Disabled/Grayed-out color
              stroke="gray"
              strokeWidth="0.01"
            />
          ) : (
            <path
              d={hiddenFilteredPath}
              fill="#E0E0E0" // Disabled/Grayed-out color for type-filtered items
              stroke="gray"
              strokeWidth="0.01"
            />
          )}
          {/* Then render visible, colored paths (will be base color for search results or active types) */}
          <path
            d={cerclePath}
            fill="#e69138"
            stroke="black"
            strokeWidth="0.01"
          />
          <path
            d={entreprisePath}
            fill="#b4a7d6"
            stroke="black"
            strokeWidth="0.01"
          />
          <path
            d={annexePath}
            fill="#a4c2f4"
            stroke="black"
            strokeWidth="0.01"
          />
          <path
            d={lieuPublicPath}
            fill="#a70744"
            stroke="black"
            strokeWidth="0.01"
          />
          <path d={oiPath} fill="#4a86e8" stroke="black" strokeWidth="0.01" />
          <path
            d={tcastPath}
            fill="#a2c4c9"
            stroke="black"
            strokeWidth="0.01"
          />
          <path
            d={logementPath}
            fill="#b6d7a8"
            stroke="black"
            strokeWidth="0.01"
          />
          {satelliteCoveragePositions.length > 0 && (
            <g pointerEvents="none">
              {satelliteCoveragePositions.map((position) => {
                const startX =
                  position.x - Math.floor(SATELLITE_COVERAGE_SIZE / 2);
                const startY =
                  position.y - Math.floor(SATELLITE_COVERAGE_SIZE / 2);
                return (
                  <rect
                    key={`satellite-${position.tileId}`}
                    x={startX}
                    y={startY}
                    width={SATELLITE_COVERAGE_SIZE}
                    height={SATELLITE_COVERAGE_SIZE}
                    fill="none"
                    stroke="#ff0000"
                    strokeWidth="0.2"
                  />
                );
              })}
            </g>
          )}
          {/* Render search path animation only if there are search results */}
          {searchTiles.length > 0 && (
            <path
              d={searchPath}
              fill="#FF0000"
              stroke="#FFF000"
              strokeWidth="0.05" // Increased stroke width
            >
              <animate
                attributeName="fill-opacity"
                values="1;0.6;1" // Less transparent at the dimmest point
                dur="1.5s" // Faster pulse
                repeatCount="indefinite"
              />
              <animate
                attributeName="stroke-opacity"
                values="1;0.6;1" // Less transparent at the dimmest point
                dur="1.5s" // Faster pulse
                repeatCount="indefinite"
              />
            </path>
          )}
        </svg>
      </Box>

      <Popper
        placement="top-start"
        disablePortal={false}
        open={openPopper}
        anchorEl={anchorEl}
        modifiers={[
          {
            name: "flip",
            enabled: false,
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: "document",
              padding: 8,
            },
          },
          {
            name: "arrow",
            enabled: false,
          },
          {
            name: "offset",
            enabled: true,
            options: {
              offset: [
                (popperPosition?.x ?? 0) - viewBoxMin * TILE_SIZE,
                -((popperPosition?.y ?? 0) - viewBoxMin * TILE_SIZE),
              ],
            },
          },
        ]}
      >
        <Box
          sx={{
            border: 1,
            borderColor: "grey.400",
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 3,
          }}
        >
          {hoveredTile?.batiment?.nom && (
            <Typography
              className="typography"
              sx={{ marginBottom: 0, fontSize: "15px" }}
            >
              {`#${hoveredTile?.lotId} - ${hoveredTile?.batiment?.nom}`}
            </Typography>
          )}

          <Typography className="typography" sx={{ fontSize: "15px" }}>
            {hoveredTile?.adresse.length
              ? hoveredTile.adresse
              : "Adresse Inconnue"}
          </Typography>
        </Box>
      </Popper>
      <DecorationModal
        bitlyId={decorations[clickedTile?.lotId ?? 0]}
        open={showDecorationModal}
        onClose={handleCloseDecoration}
      ></DecorationModal>
      <PresenceModal
        open={showPresenceModal}
        onClose={handleClosePresence}
        lotId={clickedTile?.batiment?.id ?? 0}
      ></PresenceModal>
      <Dialog
        open={openModal}
        onClose={handleClose}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography
              variant="h6"
              component="span"
              sx={{ flexGrow: 1, mr: 2 }}
            >
              {clickedTile?.adresse}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {decorations[clickedTile?.lotId ?? 0] && (
                <Tooltip title="Voir la décoration">
                  <IconButton onClick={handleShowDecoration}>
                    <HomeIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Voir les présences">
                <IconButton onClick={handleShowPresence}>
                  <PersonSearchIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <BuildingForm
            lot={clickedTile}
            handleClose={handleClose}
          ></BuildingForm>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default MapGrid;
